import { components, StringMessageErrorResponseDTO, ValidationFailedErrorResponseDTO } from "@/_apiSchema.ts";
import { ensureExhaustive } from "@/core/utils/helper.ts";
import { ErrorTranslation, FieldNameKey } from "@/lib/i18n/index.ts";

export type ValidationFailedErrorResponse = components["schemas"]["ValidationFailedErrorResponseDTO"];
export type StringMessageErrorResponse = components["schemas"]["StringMessageErrorResponseDTO"];
export type ValidationError = components["schemas"]["ValidationErrorDTO"];

export type ErrorResponse = ValidationFailedErrorResponse | StringMessageErrorResponse;

export class ApiValidationError extends Error {
    readonly name = "ApiValidationError";

    constructor(public readonly validationErrors: ValidationError[]) {
        super("API validation failed");
    }
}

export function isApiValidationError(error: unknown): error is ApiValidationError {
    return error instanceof ApiValidationError;
}

export function mapValidationErrorsToFormFields<T extends object>(
    errors: ValidationError[],
    translation: ErrorTranslation,
    allowedFields?: (keyof T & string)[],
): Partial<Record<keyof T, string>> {
    const formErrors: Partial<Record<keyof T, string>> = {};

    for (const { fieldName, errorCode } of errors) {
        if (allowedFields && !allowedFields.includes(fieldName as keyof T & string)) {
            continue;
        }
        const fieldTranslation = translation.fieldNames(fieldName as FieldNameKey);
        formErrors[fieldName as keyof T] = translation.errorCodes(errorCode, { fieldName: fieldTranslation });
    }

    return formErrors;
}

export const ApplicationError = {
    NONE: "NONE",
    PAGE_NOT_FOUND: "PAGE_NOT_FOUND",
    UNKNOWN: "UNKNOWN",
} as const;
/* eslint-disable-next-line no-redeclare -- const and type same name (idiomatic TS enum pattern) */
export type ApplicationError = (typeof ApplicationError)[keyof typeof ApplicationError];

export function translateUnknownErrorToErrorResponse(unknownError: unknown): ErrorResponse {
    let errorResponse: ErrorResponse;
    const error = unknownError as Partial<ErrorResponse> | undefined;
    const payloadType = error?.payloadType;
    switch (payloadType) {
        case ValidationFailedErrorResponseDTO.VALIDATION_ERROR:
            errorResponse = error as ValidationFailedErrorResponse;
            break;
        case StringMessageErrorResponseDTO.STRING_MESSAGE:
            errorResponse = error as StringMessageErrorResponse;
            break;
        case undefined:
            throw new Error(`Failed to determine error response due to undefined payload type`);
        default:
            // IMPORTANT: We are only making assumptions of the structure of unknownError.
            // As such, this branch may be called, and we need to provide a proper error message.
            ensureExhaustive(payloadType, `Failed to determine error response due to unknown payload type ${payloadType}`);
    }
    return errorResponse;
}

export function isValidationFailedErrorResponse(errorResponse: ErrorResponse | undefined): errorResponse is ValidationFailedErrorResponse {
    return errorResponse?.payloadType === ValidationFailedErrorResponseDTO.VALIDATION_ERROR;
}
